import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subscription } from 'rxjs';
import { ActionCardComponent } from '@shared/components/action-card/action-card.component';
import { PessoaLookupService } from '@shared/services/apis/pessoa/pessoa-lookup.service';
import { EnviarPedidoEmailDialogComponent, EnviarPedidoEmailDialogData } from '../../enviar-pedido-email-dialog/enviar-pedido-email-dialog.component';
import { PedidoEnvioView } from '../card-edit-dialog.types';

/**
 * Resultado do envio automático (automação "Enviar pedido ao fornecedor"). Distingue
 * "fornecedor sem e-mail" de "não foi possível carregar o fornecedor": culpar o cadastro
 * quando o lookup de Pessoas falhou manda o comprador arrumar o que já está certo.
 */
export type ResultadoEnvioAutomatico = 'enviado' | 'sem-email' | 'fornecedor-indisponivel' | 'nao-aplicavel';

/** Payload do envio do pedido — o shell salva a compra e chama a API com estes dados. */
export interface EnviarPedidoPayload {
  destinatario: string;
  nomeFornecedor?: string;
  documento?: string;
}

/**
 * Bloco "Envio ao fornecedor", compartilhado pelas etapas de Pedido de Compra e Aprovação.
 *
 * O botão vive na etapa dona do envio (Aprovação normalmente; Pedido quando a Aprovação está
 * desativada) e a outra mostra só o status — quem decide é `mostrarBotao`.
 *
 * Contrato:
 * - `envio` é o espelho compartilhado que vive dentro do PedidoState do shell: o filho pede o
 *   envio, o shell salva a compra + chama a API e atualiza este MESMO objeto — o badge reage na hora.
 * - `(enviarPedido)` é tratado pelo shell (onEnviarPedido), independente da etapa que emitiu.
 * - Nome/e-mail/documento do fornecedor são dados de Pessoa (a API de Compras não os tem) e são
 *   resolvidos aqui.
 */
@Component({
  selector: 'app-ced-envio-pedido',
  standalone: true,
  imports: [DatePipe, MatButtonModule, MatIconModule, MatProgressSpinnerModule, MatTooltipModule, ActionCardComponent],
  templateUrl: './ced-envio-pedido.component.html',
  styleUrl: './ced-envio-pedido.component.css',
})
export class CedEnvioPedidoComponent implements OnInit, OnDestroy {
  /** Espelho compartilhado do envio (pertence ao PedidoState do shell). */
  @Input({ required: true }) envio!: PedidoEnvioView;

  /** Só a etapa dona do envio mostra o botão; as outras ficam com o badge de status. */
  @Input() mostrarBotao = true;

  /** Trava externa do botão (ex.: alçada ainda não satisfeita). */
  @Input() habilitado = true;

  /** Texto do motivo, exibido quando o botão está visível mas travado por `habilitado`. */
  @Input() motivoBloqueio = '';

  @Output() enviarPedido = new EventEmitter<EnviarPedidoPayload>();

  // Dados do fornecedor do pedido (Pessoa), resolvidos aqui — a API de Compras não os tem.
  fornecedorNome = '';
  fornecedorEmail = '';
  fornecedorDocumento = '';

  /** O cadastro do fornecedor foi lido com sucesso (mesmo que sem e-mail). */
  private fornecedorResolvido = false;

  private readonly subs = new Subscription();

  constructor(
    private pessoaLookupService: PessoaLookupService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.carregarFornecedor();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  get envioStatus(): 'aguardando' | 'enviado' | 'confirmado' {
    if (this.envio.datetimeConfirmacao) return 'confirmado';
    if (this.envio.datetimeEnvio) return 'enviado';
    return 'aguardando';
  }

  get podeEnviar(): boolean {
    return this.habilitado && !this.envio.enviando;
  }

  abrirEnvioPedido(): void {
    if (!this.podeEnviar) return;

    const data: EnviarPedidoEmailDialogData = {
      nomeFornecedor: this.fornecedorNome || 'Fornecedor',
      destinatarioPadrao: this.fornecedorEmail,
    };
    const ref = this.dialog.open(EnviarPedidoEmailDialogComponent, { data, autoFocus: false });
    this.subs.add(
      ref.afterClosed().subscribe((destinatario?: string) => {
        if (!destinatario) return;
        this.enviarPedido.emit({
          destinatario,
          nomeFornecedor: this.fornecedorNome || undefined,
          documento: this.fornecedorDocumento || undefined,
        });
      }),
    );
  }

  /**
   * Envio disparado pela automação "Enviar pedido ao fornecedor": usa o e-mail do
   * cadastro sem abrir o diálogo. O retorno diz ao chamador o que aconteceu, para a
   * mensagem refletir o motivo real de não ter enviado.
   *
   * Não checa `podeEnviar`: quem chama acabou de aprovar o pedido e o `habilitado`
   * ainda reflete o estado anterior — o change detection não rodou.
   */
  public enviarSemConfirmacao(): ResultadoEnvioAutomatico {
    if (this.envio.enviando || this.envio.datetimeEnvio) return 'nao-aplicavel';
    if (!this.fornecedorResolvido) return 'fornecedor-indisponivel';

    const destinatario = this.fornecedorEmail.trim();
    if (!destinatario) return 'sem-email';

    this.enviarPedido.emit({
      destinatario,
      nomeFornecedor: this.fornecedorNome || undefined,
      documento: this.fornecedorDocumento || undefined,
    });
    return 'enviado';
  }

  /** Resolve nome/e-mail/documento do fornecedor do pedido (dado de Pessoa). */
  private carregarFornecedor(): void {
    const idFornecedor = this.envio.idFornecedor;
    if (!idFornecedor) return;

    this.subs.add(
      this.pessoaLookupService.getListPessoaIds({ listIdPessoa: [idFornecedor] }).subscribe({
        next: (res) => {
          if (res.isError) return;
          const p = (res.listaPessoa || []).find(x => x.idPessoa === idFornecedor);
          if (!p) return;
          this.fornecedorNome = p.nome || p.nomeFantasia || '';
          this.fornecedorEmail = p.emailNFe || '';
          this.fornecedorDocumento = p.cnpj || p.cpf || '';
          this.fornecedorResolvido = true;
        },
      }),
    );
  }
}
